import { useState, useEffect } from "react";
import Loader from "components/Loader";
import HelpCard from "components/Cards/HelpCard";
import KeywordIdeasTable from "components/Tables/KeywordIdeasTable";
import { submitGoogleAdsAnalysisRequest } from "services/buddyXApiHubService";
import MobileSupportTabBar from "components/TabBars/MobileSupportTabBar";
import { Button, Col, Form } from "react-bootstrap";
import Select from "react-select";
import Validator from "utils/Validator";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import { getInputFieldStyle, getButtonStyle, getSelectStyle } from "utils/formStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";

export default function Page() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [webSite, setWebSite] = useState("");
  const [country, setCountry] = useState({ value: "2036", label: "🇦🇺 AU" });
  const [formData, setFormData] = useState({
    country: "",
    pageUrl: "",
  });
  const [showSearchItem, setShowSearchItem] = useState("");
  const [data, setData] = useState();
  const [isResultGot, setIsResultGot] = useState(false);
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [errors, setErrors] = useState({ country: "", webSite: "" });
  const firstLevelTopic = "Content Editor";
  const secoundLevelTopic = "Keyword Discovery";
  const thirdLevelTopic = "URL";

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
    setInsufficientCreditsData(null);
  };

  const countryOptions = [
    { value: "2036", label: "🇦🇺 AU" },
    { value: "2840", label: "🇺🇸 USA" },
    { value: "2826", label: "🇬🇧 UK" },
    { value: "2392", label: "🇸🇬 SG" },
    { value: "2276", label: "🇳🇿 NZ" },
  ];

  const handleWebSiteChange = (e) => {
    setWebSite(e.target.value);
  };

  const handleCountryChange = (selectedOption) => {
    setCountry(selectedOption);
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const result = await submitGoogleAdsAnalysisRequest(
        formData,
        "keyword_ideas_url",
        handleInsufficientCredits
      );
      setShowSearchItem(formData.pageUrl);
      setData(result);
      setIsResultGot(true);
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setData([]);
    setShowSearchItem("");
    setIsResultGot(false);
    setWebSite("");
    setCountry({ value: "2036", label: "🇦🇺 AU" });
    setErrors({ country: "", webSite: "" });
    setItemsPerPage(25);
  };

  const handleItemsPerPageChange = (selectedOption) => {
    setItemsPerPage(selectedOption.value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    if (
      name === "webSite" &&
      !Validator.validateRequired(value, Validator.validateUrl)
    ) {
      error = "Please enter url";
    } else if (
      name === "country" &&
      !Validator.validateRequired(value, Validator.validateCountry)
    ) {
      error = "Select country";
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  useEffect(() => {
    let isValid =
      Validator.validateRequired(webSite, Validator.validateUrl) &&
      Validator.validateRequired(country.value, Validator.validateCountry);
    if (isValid) {
      setFormData({
        country: [country.value],
        pageUrl: webSite,
      });
    }
    setIsFormValid(isValid);
  }, [country, webSite]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      <h1 className=" d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secoundLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / {secoundLevelTopic} / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Keyword research based on given page URLs enables a reverse-engineering
        approach to SEO, analyzing existing content's performance to uncover
        high-ranking keywords, traffic drivers, and content gaps, which can
        optimize underperforming pages and inform future strategies. In the 2025
        search landscape, with heightened emphasis on E-E-A-T (Experience,
        Expertise, Authoritativeness, Trustworthiness) and AI-curated results,
        this method can increase organic visibility by 25-35% by aligning pages
        with proven query intents, reducing redundancy, and enhancing topical
        depth without starting from scratch, thereby maximizing ROI on
        established assets.
      </p>
      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={
              "Input the given page URLs into specialized tools such as Ahrefs Site Explorer, SEMrush Organic Research, or Google Search Console to extract ranking keywords, search volumes, and competitor overlaps, focusing on metrics like position, traffic potential, and difficulty. Categorize findings by intent and performance tier, identifying opportunities for expansion (e.g., long-tail variants) or refinement; map keywords back to page content for on-page updates. Regularly revisit analyses quarterly to adapt to algorithm shifts, integrating results into a centralized keyword inventory for site-wide coherence and iterative improvements."}
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className=" p-2 mb-2 rounded">
        <div className=" d-flex fw-bold mb-0">Find Your Keywords</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter a URL to get relevant keyword ideas.
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={6} className="me-2">
                  <Form.Control
                    type="text"
                    name="webSite"
                    placeholder="Enter URL"
                    value={webSite}
                    onChange={handleWebSiteChange}
                    isInvalid={!!errors.webSite}
                    onBlur={handleBlur}
                    required
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                </Col>
                <Select
                  name="country"
                  value={country}
                  onChange={handleCountryChange}
                  options={countryOptions}
                  className="me-2"
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: "200px",
                    }),
                    control: (base) => ({
                      ...base,
                      ...getSelectStyle('type6'),
                    }),
                  }}
                />
                {errors.country && (
                  <div className="text-danger">{errors.country}</div>
                )}
                <div className="col-lg-4 col-md-4 col-sm-12 mb-2 me-1">
                  <div className="d-flex justify-content-start align-items-end">
                    <Button
                      variant="dark"
                      className="me-1"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isLoading}
                      style={getButtonStyle('type6', 'primary')}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Generating...
                        </>
                      ) : (
                        "Search"
                      )}
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="me-1"
                      onClick={handleReset}
                      style={getButtonStyle('type6', 'outline')}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {showSearchItem !== "" && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="d-flex fw-bold mb-0">
              Keyword ideas for
              <span className="search-item">&nbsp;"{showSearchItem}"</span>
            </p>
            <div className="d-flex align-items-center">
              <span className="me-2">Show:</span>
              <Select
                value={{ value: itemsPerPage, label: itemsPerPage }}
                onChange={handleItemsPerPageChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 75, label: "75" }
                ]}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: "80px"
                  }),
                  control: (base) => ({
                    ...base,
                    ...getSelectStyle('type6'),
                    minHeight: "32px",
                    fontSize: "14px"
                  })
                }}
              />
            </div>
          </div>
        )}
        {isLoading && <Loader />}
        {isResultGot && <KeywordIdeasTable data={data} itemsPerPage={itemsPerPage}/>}
      </div>
      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
    </div>
  );
}
